import React from 'react'
import { DialogTitle, DialogContent, FormControlLabel, Checkbox, DialogActions, Button } from '@mui/material'
import { snakeCase } from 'lodash'
import ResponsiveDialog from 'components/ResponsiveDialog'
import { graphql, useFragment, useLazyLoadQuery } from 'react-relay'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { useSnackbar } from 'hooks/redux'
import { useNodes } from 'hooks/relay'
import { SpecializationFieldsDialog_Query } from '__generated__/SpecializationFieldsDialog_Query.graphql'
import { SpecializationFieldsDialog_UpdateUserMutation } from '__generated__/SpecializationFieldsDialog_UpdateUserMutation.graphql'
import { SpecializationFieldsDialog_User$key } from '__generated__/SpecializationFieldsDialog_User.graphql'

export interface ISpecializationFieldsDialogProps {
  user: SpecializationFieldsDialog_User$key
  onClose: () => void
}

function SpecializationFieldsDialog(props: ISpecializationFieldsDialogProps) {
  const { user: userFragmentRef, onClose } = props
  const { showSnackbarMessage } = useSnackbar()

  const user = useFragment(graphql`
    fragment SpecializationFieldsDialog_User on User {
      profile {
        industry {
          id
        }

        specializations {
          id
          name
        }
      }
    }
  `, userFragmentRef)

  const [selectedSubtypeIds, setSelectedSubtypeIds] = React.useState<string[]>(
    user?.profile?.specializations?.map(st => st.id) || [],
  )

  const { execute: updateProfile, loading } = useQuickCommit<SpecializationFieldsDialog_UpdateUserMutation>(graphql`
    mutation SpecializationFieldsDialog_UpdateUserMutation($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          profile {
            specializations {
              id
              name
            }
          }
        }
      }
    }
  `)

  const handleCheckToggle = (freelancerSubtypeId: string) => {
    if (selectedSubtypeIds.includes(freelancerSubtypeId)) {
      setSelectedSubtypeIds(selectedSubtypeIds.filter(id => id !== freelancerSubtypeId))
    } else {
      setSelectedSubtypeIds([...selectedSubtypeIds, freelancerSubtypeId])
    }
  }

  const handleSave = () => {
    updateProfile({
      input: {
        profile: {
          specializationIds: selectedSubtypeIds,
        },
      },
    }).then((result) => {
      if (result?.updateUser?.user?.profile?.specializations) {
        showSnackbarMessage('Specializations updated successfully', { level: 'success' })
        onClose()
      }
    })
  }

  const selectedIndustryId = user?.profile?.industry?.id

  const data = useLazyLoadQuery<SpecializationFieldsDialog_Query>(graphql`
    query SpecializationFieldsDialog_Query($industryId: ID) {
      industry: optionalNode(id: $industryId) {
        ... on Industry {
          specializations {
            id
            name
          }
        }
      }
    }
  `, { industryId: selectedIndustryId || null }, {
    fetchPolicy: 'store-and-network',
  })

  const editableSpecializations = useNodes(data?.industry?.specializations)

  return (
    <ResponsiveDialog open onClose={onClose} maxWidth="md">
      <DialogTitle>
        Select your specializations
      </DialogTitle>

      <DialogContent>
        {!selectedIndustryId && (
          <div>
            Select your industry to configure specializations.
          </div>
        )}

        {selectedIndustryId && editableSpecializations.map(specialization => (
          <FormControlLabel
            key={specialization.id}
            label={specialization.name}
            control={(
              <Checkbox
                key={specialization.id}
                color="primary"
                checked={selectedSubtypeIds.includes(specialization.id)}
                onChange={() => handleCheckToggle(specialization.id)}
                data-cy={`profile-specialization-${snakeCase(specialization.name)}`}
              />
            )}
          />
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} data-cy="profile-specializations-close">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={loading}
          data-cy="profile-specializations-save"
        >
          Save
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}

export default React.memo(SpecializationFieldsDialog)
